import { Button } from '@synoptic/ui-kit/button/button.js';
import {
  ModalRoot,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
} from '@synoptic/ui-kit/modal/modal.js';
import { PropsWithChildren, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiFilterInput } from '@/__generated__/graphql';
import { ConfirmModal } from '@/components/confirm-modal/confirm-modal';
import { AiFilterFormValues, AiFilter } from '../types';
import { AIFilterForm } from './ai-filter-form';

export interface ManageFilterModalProps {
  filter?: AiFilter | null;
  onSubmit: (data: AiFilterInput) => Promise<boolean>;
}

export const ManageFilterModal = ({
  children,
  filter,
  onSubmit,
}: PropsWithChildren<ManageFilterModalProps>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmLeaveModalOpen, setIsConfirmLeaveModalOpen] = useState(false);
  const formData = useForm<AiFilterFormValues>({
    defaultValues: {
      name: filter?.name ?? '',
      prompt: filter?.prompt ?? '',
    },
    mode: 'onChange',
  });
  const { isDirty, isSubmitting } = formData.formState;

  const close = () => {
    if (isSubmitting) return;
    if (isDirty) {
      setIsConfirmLeaveModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <ModalRoot
      open={isOpen}
      onOpenChange={(shouldOpen) => {
        if (!shouldOpen) {
          close();
          return;
        }
        formData.reset({
          name: filter?.name ?? '',
          prompt: filter?.prompt ?? '',
        });
        setIsConfirmLeaveModalOpen(false);
        setIsOpen(true);
      }}
    >
      <ModalTrigger asChild>{children}</ModalTrigger>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {filter ? 'Edit AI filter' : 'Create AI filter'}
          </ModalTitle>
        </ModalHeader>
        <AIFilterForm
          formData={formData}
          onCancel={close}
          onSubmit={async (data) => {
            formData.clearErrors('root');
            try {
              if (await onSubmit(data)) {
                setIsOpen(false);
              }
            } catch {
              formData.setError('root', {
                message: 'Could not save filter. Please try again.',
              });
              await formData.trigger();
            }
          }}
        />
        <ConfirmModal
          isOpen={isConfirmLeaveModalOpen}
          onOpenChange={setIsConfirmLeaveModalOpen}
          title="Discard changes?"
          description="Your changes to this filter will be lost."
          submitButton={
            <Button
              onClick={() => {
                setIsConfirmLeaveModalOpen(false);
                setIsOpen(false);
              }}
              variant="critical"
              size="small"
            >
              Discard
            </Button>
          }
        />
      </ModalContent>
    </ModalRoot>
  );
};
